import { getContrastRatio } from '@mui/material';

const DEFAULT_SHIFT_COLOR = '#446388';

/**
 * Normalize common CSS color formats for contrast calculations.
 * Returns a value MUI getContrastRatio can parse, or null if unsupported.
 */
export const normalizeCssColor = (color?: string | null): string | null => {
	if (!color || color === 'transparent') return null;

	const trimmed = color.trim();

	if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(trimmed)) {
		return trimmed;
	}

	if (/^rgba?\(/i.test(trimmed)) {
		return trimmed;
	}

	if (/^[0-9a-fA-F]{6}$/.test(trimmed)) {
		return `#${trimmed}`;
	}

	return null;
};

const resolveBackgroundColor = (color?: string | null): string =>
	normalizeCssColor(color) || DEFAULT_SHIFT_COLOR;

/**
 * Pick black or white text/icon color with the highest WCAG contrast against the background.
 */
export const getBestContrastTextColor = (backgroundColor?: string | null): string => {
	const bg = resolveBackgroundColor(backgroundColor);

	try {
		const whiteContrast = getContrastRatio(bg, '#ffffff');
		const blackContrast = getContrastRatio(bg, '#000000');
		return whiteContrast >= blackContrast ? '#ffffff' : '#000000';
	} catch {
		return '#ffffff';
	}
};

/** True when dark foreground (black) contrasts better — i.e. the background reads as light. */
export const isLightBackground = (backgroundColor?: string | null): boolean =>
	getBestContrastTextColor(backgroundColor) === '#000000';

export type ShiftActionIconColors = {
	copy: string;
	delete: string;
	badgeBg: string;
	badgeBorder: string;
	hoverBg: string;
};

export const SHIFT_ACTION_ICON_SIZE = 22;
export const SHIFT_ACTION_ICON_FONT_SIZE = 14;

/**
 * Action icon colors for copy/delete controls on shift blocks.
 */
export const getShiftActionIconColors = (
	backgroundColor?: string | null
): ShiftActionIconColors => {
	const isLight = isLightBackground(backgroundColor);

	return {
		copy: isLight ? 'rgb(68, 99, 136)' : '#ffffff',
		delete: isLight ? '#d32f2f' : '#ff8a80',
		badgeBg: isLight ? 'rgba(255, 255, 255, 0.94)' : 'rgba(32, 32, 32, 0.88)',
		badgeBorder: isLight ? 'rgba(0, 0, 0, 0.14)' : 'rgba(255, 255, 255, 0.22)',
		hoverBg: isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.2)',
	};
};

type ShiftActionIconKind = 'copy' | 'delete';

/**
 * Centered circular badge styles for MUI IconButtons on shift blocks.
 */
export const getShiftActionIconButtonSx = (
	backgroundColor?: string | null,
	kind: ShiftActionIconKind = 'copy'
) => {
	const colors = getShiftActionIconColors(backgroundColor);
	const iconColor = kind === 'delete' ? colors.delete : colors.copy;

	return {
		width: SHIFT_ACTION_ICON_SIZE,
		height: SHIFT_ACTION_ICON_SIZE,
		minWidth: SHIFT_ACTION_ICON_SIZE,
		minHeight: SHIFT_ACTION_ICON_SIZE,
		padding: 0,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		boxSizing: 'border-box',
		borderRadius: '50%',
		lineHeight: 0,
		color: iconColor,
		backgroundColor: colors.badgeBg,
		border: `1px solid ${colors.badgeBorder}`,
		boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)',
		'& .MuiSvgIcon-root': {
			fontSize: SHIFT_ACTION_ICON_FONT_SIZE,
			display: 'block',
		},
		'&:hover': {
			backgroundColor: colors.hoverBg,
		},
	};
};

export type ShiftIconBadgeStyle = {
	display: 'inline-flex';
	alignItems: 'center';
	justifyContent: 'center';
	borderRadius: string;
	backgroundColor: string;
	border: string;
	boxShadow: string;
	lineHeight: number;
	width: number;
	height: number;
	minWidth: number;
	minHeight: number;
};

/**
 * Contrasting circular badge behind small PNG/MUI icons on colored shift blocks.
 */
export const getShiftIconBadgeStyle = (
	backgroundColor?: string | null,
	sizePx = 18
): ShiftIconBadgeStyle => {
	const isLight = isLightBackground(backgroundColor);

	return {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: '50%',
		backgroundColor: isLight ? 'rgba(255, 255, 255, 0.94)' : 'rgba(32, 32, 32, 0.88)',
		border: isLight ? '1px solid rgba(0, 0, 0, 0.14)' : '1px solid rgba(255, 255, 255, 0.22)',
		boxShadow: '0 1px 2px rgba(0, 0, 0, 0.22)',
		lineHeight: 0,
		width: sizePx + 4,
		height: sizePx + 4,
		minWidth: sizePx + 4,
		minHeight: sizePx + 4,
	};
};
